// NOTA: los imports de este archivo llevan extension .js a proposito. Vite
// resuelve sin extension, pero `node --test` no: sin ella los tests no pueden
// cargar este modulo.
//
// Logica pura de Reportes Inteligentes: rangos de fechas, claves de fecha del
// POS y agregaciones del periodo. Vive aca y no dentro de la pantalla para que
// se pueda testear sin montar React — ver tests/reportes-rangos.test.mjs.
//
// Convenciones que NO se pueden romper:
// - El POS guarda created_at con "hora forjada" (hora local + sufijo Z). Las
//   claves de fecha salen de slice(0,10) del string, nunca de getters locales.
// - Las ventas anuladas y los presupuestos se excluyen SIEMPRE.
// - En sale_items los combos guardan 1 fila `parent` (sin costo propio) + N
//   filas `component`. Para cualquier costo/margen se saltea la parent.

import { getClientReceivableValue, getProviderBalanceValue } from './balanceValues.js';
import { getProductStockForSite } from './productCatalog.js';
import { sanitizeProductDisplayName } from './productLabels.js';
import { separarGastos } from './puntoEquilibrio.js';

// Nombre de producto saneado, con fallback. Igual criterio que la pantalla.
const safeName = (raw, fallback = 'Sin nombre') =>
  sanitizeProductDisplayName(raw || fallback, fallback);

// Ventanas moviles (days) + cortes de calendario (kind). El corte natural de
// un comercio es el mes, no una ventana de 30 dias: "Este mes" y "Mes anterior"
// son los que se miran para cerrar numeros y comparar contra el mes previo.
export const PERIODS = [
  { id: 'today', label: 'Hoy', days: 1 },
  { id: 'week', label: '7 días', days: 7 },
  { id: 'month', label: '30 días', days: 30 },
  { id: 'quarter', label: '90 días', days: 90 },
  { id: 'this-month', label: 'Este mes', kind: 'month-to-date' },
  { id: 'last-month', label: 'Mes anterior', kind: 'last-month' },
  { id: 'year', label: 'Este año', kind: 'year-to-date' },
];

// Estados de ventas a excluir: 3 variantes de cancelado + presupuestos
export const INVALID_SALE_STATUSES = ['canceled', 'cancelled', 'anulada', 'quote'];
export const SALE_STATUS_NOT_IN = `(${INVALID_SALE_STATUSES.map((s) => `"${s}"`).join(',')})`;

export const toLocalDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

// Corre un mes/anio hacia atras cuidando los fines de mes: sin el clamp,
// new Date(y, 1, 31) (31 de febrero) se desborda al 3 de marzo y el periodo
// anterior queda corrido.
export const shiftMonthsClamped = (date, months) => {
  const y = date.getFullYear();
  const m = date.getMonth() - months;
  const lastDay = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(date.getDate(), lastDay));
};

export const buildPresetRange = (cfg) => {
  const today = new Date();
  if (cfg?.kind === 'month-to-date') {
    return {
      from: toLocalDateString(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: toLocalDateString(today),
    };
  }
  if (cfg?.kind === 'last-month') {
    // Mes calendario completo anterior: del 1 al ultimo dia (day 0 del mes actual).
    return {
      from: toLocalDateString(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      to: toLocalDateString(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
  }
  if (cfg?.kind === 'year-to-date') {
    return {
      from: toLocalDateString(new Date(today.getFullYear(), 0, 1)),
      to: toLocalDateString(today),
    };
  }
  const days = Math.max(1, Number(cfg?.days || 1));
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return { from: toLocalDateString(start), to: toLocalDateString(end) };
};

export const buildRangeFromDates = (fromStr, toStr) => {
  const start = new Date(`${fromStr}T00:00:00`);
  const end = new Date(`${toStr}T23:59:59.999`);
  const dayMs = 24 * 60 * 60 * 1000;
  // `end` ya es 23:59:59.999 del ultimo dia, asi que la diferencia en dias YA
  // cuenta el ultimo dia completo (10/9 00:00 -> 16/9 23:59 = 6,99 -> 7 dias).
  // El `+ 1` que habia aca sumaba un dia fantasma: un rango de 7 dias daba 8, y
  // "Hoy" daba 2. Eso corria el periodo anterior (comparaba 7 dias contra 8, y
  // "Hoy" contra los dos dias previos sumados) y dejaba todos los deltas del
  // modulo sistematicamente pesimistas.
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / dayMs));
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));
  prevStart.setHours(0, 0, 0, 0);
  return { start, end, prevStart, prevEnd, days };
};

// Periodo anterior con el que se compara. Para los cortes de calendario NO
// sirve "N dias hacia atras": comparar 1-16/9 contra 16-31/8 mezcla dos
// quincenas distintas. El mes en curso se compara contra el mismo tramo del mes
// pasado, el mes cerrado contra el mes calendario anterior completo, y el anio
// contra el mismo tramo del anio pasado.
export const buildPrevRangeDates = (fromStr, toStr, periodId) => {
  const kind = PERIODS.find((p) => p.id === periodId)?.kind;
  const from = new Date(`${fromStr}T12:00:00`);
  const to = new Date(`${toStr}T12:00:00`);

  if (kind === 'month-to-date') {
    return {
      prevStartDate: toLocalDateString(new Date(from.getFullYear(), from.getMonth() - 1, 1)),
      prevEndDate: toLocalDateString(shiftMonthsClamped(to, 1)),
    };
  }
  if (kind === 'last-month') {
    return {
      prevStartDate: toLocalDateString(new Date(from.getFullYear(), from.getMonth() - 1, 1)),
      prevEndDate: toLocalDateString(new Date(from.getFullYear(), from.getMonth(), 0)),
    };
  }
  if (kind === 'year-to-date') {
    return {
      prevStartDate: toLocalDateString(new Date(from.getFullYear() - 1, 0, 1)),
      prevEndDate: toLocalDateString(shiftMonthsClamped(to, 12)),
    };
  }

  const { prevStart, prevEnd } = buildRangeFromDates(fromStr, toStr);
  return { prevStartDate: toLocalDateString(prevStart), prevEndDate: toLocalDateString(prevEnd) };
};

export const buildSaleQueryBounds = (fromStr, toStr) => {
  // El POS guarda created_at con "hora forjada": hora local + sufijo Z (UTC fijo).
  // El Historial de Ventas filtra exactamente así: T00:00:00.000Z / T23:59:59.999Z.
  // Reproducimos el mismo criterio para que Reportes y Historial coincidan.
  return {
    startIso: `${fromStr}T00:00:00.000Z`,
    endIso: `${toStr}T23:59:59.999Z`,
  };
};

// Serie diaria con TODOS los dias del rango, incluidos los que no vendieron
// (si no, el grafico se come los huecos y miente sobre la continuidad).
export const buildDailySeries = (sales, fromStr, toStr) => {
  const buckets = new Map();
  const cursor = new Date(`${fromStr}T12:00:00`);
  const endCursor = new Date(`${toStr}T12:00:00`);
  while (cursor <= endCursor) {
    const key = toLocalDateString(cursor);
    buckets.set(key, { fecha: key, ventas: 0, tickets: 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  (sales || []).forEach((s) => {
    const key = getSaleDateKey(s);
    if (!buckets.has(key)) return;
    const b = buckets.get(key);
    b.ventas += Number(s.total || 0);
    b.tickets += 1;
  });
  return Array.from(buckets.values());
};

export const getSaleDateKey = (sale) => {
  // Misma lógica que toDateOnly() en VentasHistorial: slice(0,10) del string.
  // NO convertir a hora local: el POS usa hora forjada (local interpretada como UTC).
  const fechaVenta = String(sale?.fecha_venta || '').slice(0, 10);
  if (DATE_ONLY_RE.test(fechaVenta)) return fechaVenta;

  const rawTs = String(sale?.created_at || '').slice(0, 10);
  if (DATE_ONLY_RE.test(rawTs)) return rawTs;
  return '';
};

export const isDateKeyInRange = (key, fromStr, toStr) =>
  DATE_ONLY_RE.test(String(key || '')) && key >= fromStr && key <= toStr;

export const deltaPercent = (current, previous) => {
  const a = Number(current || 0);
  const b = Number(previous || 0);
  if (Math.abs(b) < 0.0001) return a > 0 ? 100 : 0;
  return ((a - b) / Math.abs(b)) * 100;
};

// Extrae los días de condición de pago (ej: "30 días" → 30) para calcular el
// vencimiento de una compra. Mismo criterio que usa FlujoFondos.
export const getCompraDueDays = (condicionPago) => {
  const m = String(condicionPago || '').match(/(\d+)/);
  return m ? Math.max(0, Number(m[1]) || 0) : 0;
};

// Calcula la fecha de vencimiento de una compra: fecha + días de condición.
export const getCompraDueDate = (compra) => {
  const baseStr = String(compra?.fecha || compra?.created_at || '').slice(0, 10);
  if (!baseStr) return null;
  const base = new Date(`${baseStr}T12:00:00`);
  if (Number.isNaN(base.getTime())) return null;
  const days = getCompraDueDays(compra?.condicion_pago);
  if (days > 0) base.setDate(base.getDate() + days);
  return base;
};


// Totales del periodo. `raw` es el bloque de datos ya filtrado por rango y
// sucursal que arma la pantalla.
export const computeMetrics = (raw) => {
  const ventasTotal = raw.sales.reduce((acc, s) => acc + Number(s.total || 0), 0);
  const ticketsCount = raw.sales.length;
  const ticketPromedio = ticketsCount > 0 ? ventasTotal / ticketsCount : 0;

  // Combos: sale_items guarda 1 fila `parent` (resumen de la promo, sin costo
  // propio confiable) + N filas `component` con el costo real de cada
  // ingrediente. Contar la parent duplica el costo del combo y deja el
  // beneficio del periodo por debajo del real, sin cuadrar contra la tabla
  // de "Rentabilidad por producto". Mismo criterio que rentabilidadProductos,
  // prevCmv y RentabilidadVentas.jsx (ver bug-promo-rentabilidad).
  const cmv = raw.saleItems.reduce((acc, item) => {
    if (item.combo_role === 'parent') return acc;
    const qty = Number(item.quantity || 0);
    const cost = Number(item.producto?.cost_price || 0);
    return acc + qty * cost;
  }, 0);
  const beneficio = ventasTotal - cmv;
  const margenPct = ventasTotal > 0 ? (beneficio / ventasTotal) * 100 : 0;

  // getClientReceivableValue, no getClientBalanceValue: el saldo del consumidor
  // final generico no es plata por cobrar (ver balanceValues.js).
  const aCobrar = raw.clients.reduce((acc, c) => {
    const balance = getClientReceivableValue(c);
    return balance > 0 ? acc + balance : acc;
  }, 0);

  const aPagar = raw.providers.reduce((acc, p) => {
    const balance = getProviderBalanceValue(p);
    return balance > 0 ? acc + balance : acc;
  }, 0);

  const gastosPeriodo = raw.gastos.reduce((acc, g) => acc + Number(g.monto || 0), 0);
  // Fijos / variables para el punto de equilibrio (utils/puntoEquilibrio.js).
  const { fijos: gastosFijos, variables: gastosVariables } = separarGastos(raw.gastos);

  // Resultado neto = bruto - gastos del periodo. El bruto solo no dice si el
  // negocio gano plata: los gastos ya estaban cargados y nunca se restaban.
  const resultadoNeto = beneficio - gastosPeriodo;

  return {
    ventasTotal,
    ticketsCount,
    ticketPromedio,
    cmv,
    beneficio,
    margenPct,
    aCobrar,
    aPagar,
    gastosPeriodo,
    gastosFijos,
    gastosVariables,
    resultadoNeto,
    tieneGastos: raw.gastos.length > 0,
  };
};

// Rentabilidad por producto del periodo (lista completa, ordenada por beneficio).
// Descuentos / recargos de la venta repartidos entre sus ítems.
// sale_items guarda el precio de lista; el descuento por medio de pago (ej. 10%
// efectivo) o el recargo (ej. 15% crédito) solo queda en sales.total. Sin este
// prorrateo la rentabilidad por producto suma precio de lista y los márgenes
// salen inflados (Don Saro, oct-2026: +$540 mil, ~10 puntos de margen).
// Devuelve Map(sale_id → total de la venta / suma de sus ítems). Las filas
// parent de combo no cuentan (su venta real está en los componentes). Si la
// venta no trae total o sus ítems suman 0, el factor es 1 (sin ajuste).
export const saleItemAmount = (item) =>
  Number(item?.subtotal ?? Number(item?.quantity || 0) * Number(item?.unit_price || 0)) || 0;

export const buildSaleAdjustmentFactors = (saleItems) => {
  const sums = new Map();
  const totals = new Map();
  (saleItems || []).forEach((item) => {
    if (item?.combo_role === 'parent') return;
    const saleId = item?.sale_id ?? item?.venta?.id;
    if (!saleId) return;
    sums.set(saleId, (sums.get(saleId) || 0) + saleItemAmount(item));
    const total = item?.venta?.total;
    if (total != null && Number.isFinite(Number(total))) totals.set(saleId, Number(total));
  });
  const factors = new Map();
  sums.forEach((sum, saleId) => {
    const total = totals.get(saleId);
    factors.set(saleId, sum > 0 && total != null && total >= 0 ? total / sum : 1);
  });
  return factors;
};

export const saleAdjustmentFactor = (factors, item) =>
  factors.get(item?.sale_id ?? item?.venta?.id) ?? 1;

export const computeRentabilidadProductos = (saleItems) => {
  const map = new Map();
  const factors = buildSaleAdjustmentFactors(saleItems);
  (saleItems || []).forEach((item) => {
      if (item.combo_role === 'parent') return;
      const p = item.producto;
      if (!p?.id) return;
      const qty = Number(item.quantity || 0);
      const subtotal = saleItemAmount(item) * saleAdjustmentFactor(factors, item);
      const cost = qty * Number(p.cost_price || 0);
      const entry = map.get(p.id) || {
        id: p.id,
        nombre: safeName(item.product_name_snapshot || p.name),
        cant: 0,
        venta: 0,
        cmv: 0,
      };
      entry.cant += qty;
      entry.venta += subtotal;
      entry.cmv += cost;
      map.set(p.id, entry);
    });
    return Array.from(map.values())
      .map((e) => ({
        ...e,
        beneficio: e.venta - e.cmv,
        margenPct: e.venta > 0 ? ((e.venta - e.cmv) / e.venta) * 100 : 0,
      }))
      .sort((a, b) => b.beneficio - a.beneficio);
};

// Comparativa por sucursal del periodo. Devuelve [] cuando no aplica (una sola
// sucursal, o una sucursal ya filtrada: ahi los datos en memoria son de esa sola).
export const computeComparativaSucursales = ({ raw, siteFilter, sites }) => {

  if (siteFilter || !Array.isArray(sites) || sites.length < 2) return [];

  const SIN_ASIGNAR = '__sin_sucursal__';
  const filas = new Map();
  const fila = (id) => {
    const key = id ? String(id) : SIN_ASIGNAR;
    if (!filas.has(key)) {
      filas.set(key, {
        id: key,
        nombre:
          key === SIN_ASIGNAR
            ? 'Sin sucursal'
            : sites.find((x) => String(x.id) === key)?.nombre || 'Sucursal sin nombre',
        ventas: 0,
        tickets: 0,
        cmv: 0,
        gastos: 0,
      });
    }
    return filas.get(key);
  };

  (raw.sales || []).forEach((s) => {
    const f = fila(s.site_id);
    f.ventas += Number(s.total || 0);
    f.tickets += 1;
  });

  (raw.saleItems || []).forEach((item) => {
    // Mismo criterio de combos que el CMV del KPI: la fila parent duplicaria.
    if (item.combo_role === 'parent') return;
    const f = fila(item.venta?.site_id);
    f.cmv += Number(item.quantity || 0) * Number(item.producto?.cost_price || 0);
  });

  (raw.gastos || []).forEach((g) => {
    fila(g.sitio_id).gastos += Number(g.monto || 0);
  });

  return Array.from(filas.values())
    .map((f) => {
      const beneficio = f.ventas - f.cmv;
      return {
        ...f,
        ticketPromedio: f.tickets > 0 ? f.ventas / f.tickets : 0,
        beneficio,
        margenPct: f.ventas > 0 ? (beneficio / f.ventas) * 100 : 0,
        resultadoNeto: beneficio - f.gastos,
      };
    })
    .sort((a, b) => b.ventas - a.ventas);
};

// Rotacion y dias de cobertura por producto. `dias` es el largo del periodo:
// la venta diaria sale de ahi, asi que un largo mal calculado desvirtua toda
// la columna de cobertura (ver el +1 que habia en buildRangeFromDates).
export const computeRotacion = ({ products, rentabilidadProductos, siteId, dias }) => {

  const soldMap = new Map(rentabilidadProductos.map((r) => [r.id, r.cant]));
  const stockSiteId = siteId || null;
  const diasPeriodo = Math.max(1, Number(dias) || 1);
  return (products || [])
    .map((p) => {
      const vendido = Number(soldMap.get(p.id) || 0);
      const stock = getProductStockForSite(p, stockSiteId);
      const rot = stock > 0 ? vendido / stock : 0;
      const ventaDiaria = vendido / diasPeriodo;
      return {
        id: p.id,
        nombre: safeName(p.name),
        vendido,
        stock,
        rotacion: rot,
        // null = no vendió nada en el período: la cobertura es infinita, que
        // es justamente lo que marca la columna "Inmovilizado".
        diasCobertura: ventaDiaria > 0 ? stock / ventaDiaria : null,
        sinRotacion: stock > 0 && vendido <= 0,
      };
    })
    .filter((r) => r.stock > 0 || r.vendido > 0)
    .sort((a, b) => {
      const ca = a.diasCobertura ?? Number.POSITIVE_INFINITY;
      const cb = b.diasCobertura ?? Number.POSITIVE_INFINITY;
      if (ca !== cb) return ca - cb;
      return b.vendido - a.vendido;
    });
};
